import Catalog20 from '@carbon/icons-react/es/catalog/20';
import { List } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { DateType } from 'api/models';
import { PersonalTask } from 'api/personal-tasks';
import { IconShell } from 'components/atoms/icon-shell';
import { PersonalTasksEmptyState } from 'components/organisms/personal-tasks/empty-state';
import {
  createPersonalTaskSagaAction,
  deletePersonalTaskSagaAction,
  updatePersonalTaskSagaAction,
} from 'data/personal-tasks/actions';
import { selectPersonalTasks } from 'data/personal-tasks/selectors';
import { PersonalTaskItem } from './personal-task-item';
import styles from './personal-tasks.module.css';

interface PersonalTasksProps {
  data: PersonalTask[];
}

export const PersonalTasks: FC<PersonalTasksProps> = () => {
  const dispatch = useDispatch();

  const { data, current, deleting, creating, createError } =
    useSelector(selectPersonalTasks);

  const updatePersonalTask = (id, _data: Partial<PersonalTask>) => {
    dispatch(
      updatePersonalTaskSagaAction({
        id,
        data: _data,
      }),
    );
  };

  const handleDueDateChange = (
    dueDate: DateType | null,
    record: PersonalTask,
  ) => {
    updatePersonalTask(record.personalTaskId, { dueDate });
  };

  const handleStatusChange = (e, record: PersonalTask) =>
    updatePersonalTask(record.personalTaskId, {
      status: e.target.checked ? 'Completed' : 'To do',
    });

  const handleTaskNameChange = (personalTaskName, record: PersonalTask) => {
    if (personalTaskName !== record.personalTaskName) {
      updatePersonalTask(record.personalTaskId, {
        personalTaskName,
      });
    }
  };

  const handleDelete = useCallback(
    (record: PersonalTask) => {
      return dispatch(deletePersonalTaskSagaAction(record));
    },
    [dispatch],
  );

  const DEFAULT_PERSONAL_TASK = {
    personalTaskId: 0,
    personalTaskName: '',
  };

  const [newPersonalTask, setNewPersonalTask] = useState<PersonalTask>(
    DEFAULT_PERSONAL_TASK,
  );

  useEffect(() => {
    if (!creating && !createError) {
      setNewPersonalTask({
        personalTaskId: 0,
        personalTaskName: '',
      });
    }
  }, [creating, createError]);

  const handleOnAdd = (personalTaskName: string) => {
    if (personalTaskName && !creating) {
      dispatch(createPersonalTaskSagaAction({ personalTaskName }));
    }
  };

  const [canShowEmptyState, setCanShowEmptyState] = useState(!data.length);

  return (
    <div className={styles.wrapper}>
      <div className={styles.personal_tasks_header}>
        <IconShell
          size="large"
          icon={<Catalog20 className={styles.personal} />}
        />
        <strong>
          <FormattedMessage id="personal_tasks" />
        </strong>
      </div>
      {!canShowEmptyState && (
        <div className={styles.list_item}>
          <PersonalTaskItem
            data={newPersonalTask}
            handleTaskNameChange={handleOnAdd}
            isCreating={creating}
            isDeleting={false}
          />
        </div>
      )}

      {data.length ? (
        <List
          className={styles.list}
          dataSource={data}
          renderItem={(record: PersonalTask) => (
            <List.Item key={record.personalTaskId} className={styles.list_item}>
              <PersonalTaskItem
                data={record}
                handleTaskNameChange={handleTaskNameChange}
                handleStatusChange={handleStatusChange}
                handleDueDateChange={handleDueDateChange}
                onDelete={handleDelete}
                isDeleting={
                  deleting && record.personalTaskId === current?.personalTaskId
                }
                isCreating={creating && !record.personalTaskId}
              />
            </List.Item>
          )}
        />
      ) : (
        canShowEmptyState && (
          <PersonalTasksEmptyState
            onClick={() => setCanShowEmptyState(false)}
          />
        )
      )}
    </div>
  );
};
